import { useEffect, useState } from "react";
import * as _ from "lodash";

import { TabsProps } from "@ctra/components";

import { useFarm } from "@farms";
import { TargetKey, useSavedCharts } from "@analytics";

/**
 * Custom hook to handle the tab logic
 * @param list
 * @returns
 */
export const useTabs = (list: TabsProps["items"]) => {
  const { farm } = useFarm();
  const [activeKey, setActiveKey] = useState(_.first(list)?.key);
  const [items, setItems] = useState(list || []);
  const {
    api: { addTab, removeTab }
  } = useSavedCharts();

  useEffect(() => {
    if (list) {
      setItems(list);
    }
  }, [list]);

  /**
   * Set the first tab as active when farm is switched
   */
  useEffect(() => {
    setActiveKey(_.first(list)?.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm]);

  /**
   * Set the active key
   */
  const onChange = (newActiveKey: string) => setActiveKey(newActiveKey);

  /**
   * Add a new tab with no charts
   * @param name
   */
  const add = (name: string) => {
    const newPanes = [...items];
    newPanes.push({ label: name, key: name });
    setItems(newPanes);
    setActiveKey(name);
  };

  /**
   * Remove a tab
   * @param targetKey
   */
  const remove = (targetKey: TargetKey) => {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    items.forEach((item, i) => {
      if (item.key === targetKey) {
        lastIndex = i - 1;
      }
    });
    const newPanes = items.filter((item) => item.key !== targetKey);
    if (newPanes.length && newActiveKey === targetKey) {
      if (lastIndex >= 0) {
        newActiveKey = newPanes[lastIndex].key;
      } else {
        newActiveKey = newPanes[0].key;
      }
    }
    setItems(newPanes);
    setActiveKey(newActiveKey);
  };

  /**
   * Add or remove a tab
   * @param targetKey
   * @param action
   * @param name
   */
  const onEdit = (targetKey: TargetKey, action: "add" | "remove", name?: string) => {
    if (action === "add" && name) {
      addTab(name);
      add(name);
    } else {
      removeTab(targetKey);
      remove(targetKey);
    }
  };

  return { onChange, onEdit, add, remove, activeKey, setActiveKey, items };
};
